import { useCallback } from 'react';
import {
    processPunctuation,
    containsPunctuationCommands,
    processMedicalText
} from '../utils/punctuationUtils';

export interface VoiceInputHandler {
    handleVoiceInput: (text: string, isFinal?: boolean) => void;
}

export const useVoiceInputHandler = (
    lastFocusedRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>,
    lastInterimTextRef: React.MutableRefObject<string>,
    focusNextInput: () => boolean,
    isNavigationCommand: (text: string) => boolean,
    updateVoiceInputTime: () => void,
    setHasChanges: (hasChanges: boolean) => void,
    voiceCommandCache: React.MutableRefObject<{ previousValue: string | null }>,
    focusFirstInput: () => boolean,
    focusLastInput: () => boolean
): VoiceInputHandler => {

    const handleNumericInput = useCallback((
        target: HTMLInputElement,
        text: string,
        isFinal: boolean
    ): boolean => {
        if (target.type !== 'number' || !isFinal) return false;

        const numericText = text.trim();
        if (!/^-?\d+(\.\d+)?$/.test(numericText)) return false;

        const numericValue = Number(numericText);
        voiceCommandCache.current.previousValue = target.value;
        target.value = numericValue.toString();
        target.dispatchEvent(new Event('input', { bubbles: true }));
        setHasChanges(true);
        return true;
    }, [setHasChanges, voiceCommandCache]);

    const handleTextInput = useCallback((
        target: HTMLInputElement | HTMLTextAreaElement,
        text: string,
        isFinal: boolean
    ) => {
        if (isFinal) {
            const processedText = processMedicalText(text);
            const start = target.selectionStart ?? 0;
            const end = target.selectionEnd ?? 0;
            const value = target.value;

            const currentInterim = lastInterimTextRef.current;
            let cleanValue = value;
            if (currentInterim && value.endsWith(currentInterim)) {
                cleanValue = value.slice(0, -currentInterim.length);
            }

            voiceCommandCache.current.previousValue = cleanValue;
            const updated = cleanValue.slice(0, start) + processedText + cleanValue.slice(end);
            target.value = updated;
            target.dispatchEvent(new Event('input', { bubbles: true }));

            const newCaret = start + processedText.length;
            target.setSelectionRange(newCaret, newCaret);
            setHasChanges(true);
            (lastInterimTextRef as any).current = '';
        } else {
            const processedText = containsPunctuationCommands(text) ? processPunctuation(text) : text;

            const start = target.selectionStart ?? 0;
            const end = target.selectionEnd ?? 0;
            const value = target.value;

            // Remove previous interim text
            const currentInterim = lastInterimTextRef.current;
            let cleanValue = value;
            if (currentInterim && value.endsWith(currentInterim)) {
                cleanValue = value.slice(0, -currentInterim.length);
            }

            const updated = cleanValue.slice(0, start) + processedText + cleanValue.slice(end);
            target.value = updated;
            target.setSelectionRange(start, start + processedText.length);
            lastInterimTextRef.current = processedText;
        }
    }, [lastInterimTextRef, setHasChanges, processMedicalText, containsPunctuationCommands, processPunctuation, voiceCommandCache]);

    const handleUndo = (target: HTMLInputElement | HTMLTextAreaElement) => {
        const prev = voiceCommandCache.current.previousValue;
        if (prev !== null) {
            target.value = prev;
            target.dispatchEvent(new Event('input', { bubbles: true }));
            setHasChanges(true);
        }
    };

    const handleSelect = (target: HTMLInputElement | HTMLTextAreaElement, phrase: string) => {
        const index = target.value.toLowerCase().indexOf(phrase.toLowerCase());
        if (index !== -1) {
            target.setSelectionRange(index, index + phrase.length);
        }
    };

    const handleDeleteLast = (target: HTMLInputElement | HTMLTextAreaElement, type: 'word' | 'phrase') => {
        const value = target.value.trimEnd();
        let newValue = value;

        if (type === 'word') {
            newValue = value.replace(/\s*\S+$/, '');
        } else {
            const lastComma = value.lastIndexOf(',');
            const lastPeriod = value.lastIndexOf('.');
            const cutIndex = Math.max(lastComma, lastPeriod);
            newValue = cutIndex !== -1 ? value.slice(0, cutIndex + 1) : '';
        }

        voiceCommandCache.current.previousValue = target.value;
        target.value = newValue;
        target.dispatchEvent(new Event('input', { bubbles: true }));
        setHasChanges(true);
    };

    const handleVoiceInput = useCallback((text: string, isFinal: boolean = true) => {
        updateVoiceInputTime();
        const lowerText = text.trim().toLowerCase();
        const focused = lastFocusedRef.current;

        if (!focused) return;

        // Handle navigation commands
        if (lowerText === 'go to first field' && isFinal) {
            focusFirstInput();
            return;
        }

        if (lowerText === 'go to last field' && isFinal) {
            focusLastInput();
            return;
        }

        if (lowerText === 'undo' && isFinal && (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement)) {
            handleUndo(focused);
            return;
        }

        if (lowerText.startsWith('select ') && isFinal) {
            const phrase = lowerText.replace('select ', '').trim();
            if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
                handleSelect(focused, phrase);
            }
            return;
        }

        if (lowerText === 'delete last word' && isFinal) {
            if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
                handleDeleteLast(focused, 'word');
            }
            return;
        }

        if (lowerText === 'delete last phrase' && isFinal) {
            if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
                handleDeleteLast(focused, 'phrase');
            }
            return;
        }

        // Navigation fallback
        if (isNavigationCommand(lowerText)) {
            if (isFinal) {
                focusNextInput();
            }
            return;
        }

        // Input processing
        if (focused instanceof HTMLInputElement && handleNumericInput(focused, text, isFinal)) {
            return;
        }

        if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
            handleTextInput(focused, text, isFinal);
        }

    }, [
        lastFocusedRef,
        lastInterimTextRef,
        handleTextInput,
        handleNumericInput,
        handleDeleteLast,
        handleSelect,
        handleUndo,
        isNavigationCommand,
        updateVoiceInputTime,
        setHasChanges,
        voiceCommandCache,
        focusNextInput,
        focusFirstInput,
        focusLastInput
    ]);

    return {
        handleVoiceInput
    };
};
